import { AuthenticationError } from '~/errors';
import { sessionStore } from '~/store/sessionStore';
import { createLogger } from '~/utils/logging';

const log = createLogger('TokenManager');

class TokenManager {
  private accessToken: string | null = null;
  private refreshPromise: Promise<string> | null = null;
  private apiUrl: string | null = null;

  // After a refresh fails, all subsequent refresh() calls fail immediately without
  // a network request. This prevents cascading 401 → refresh loops that happen when
  // React re-renders across multiple layouts after logout — each re-render triggers
  // useUserProfile to re-fetch, which gets a 401, which calls refresh(), which would
  // otherwise make a redundant network call that's guaranteed to fail.
  // Reset to false on setToken() (i.e., successful login).
  private refreshFailed = false;

  initialize(apiUrl: string): void {
    this.apiUrl = apiUrl;
    // Load persisted token from sessionStore.
    const persistedToken = sessionStore.getState().token;
    if (persistedToken) {
      this.accessToken = persistedToken;
      log.debug('Initialized with persisted token');
    } else {
      log.debug('Initialized without token');
    }
  }

  getToken(): string | null {
    return this.accessToken;
  }

  setToken(token: string): void {
    this.accessToken = token;
    this.refreshFailed = false;
    sessionStore.getState().setToken(token);
    log.debug('Token set');
  }

  async refresh(): Promise<string> {
    if (this.refreshFailed) {
      throw new AuthenticationError('Token refresh previously failed');
    }

    if (this.refreshPromise) {
      log.debug('Refresh already in progress, deduplicating');
      return this.refreshPromise;
    }

    this.refreshPromise = this.doRefresh();
    try {
      return await this.refreshPromise;
    } catch (error) {
      this.refreshFailed = true;
      throw error;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async doRefresh(): Promise<string> {
    if (!this.apiUrl) {
      throw new AuthenticationError('TokenManager not initialized');
    }

    log.info('Refreshing access token');
    try {
      const response = await fetch(`${this.apiUrl}/auth/refreshToken`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
        credentials: 'include',
      });

      if (!response.ok) {
        log.warn('Refresh token request failed', { status: response.status });
        this.clear();
        throw new AuthenticationError('Failed to refresh token', { statusCode: response.status });
      }

      const data = await response.json();
      this.setToken(data.token);
      log.info('Access token refreshed successfully');
      return data.token;
    } catch (error) {
      if (error instanceof AuthenticationError) {
        throw error;
      }
      log.error('Unexpected error during token refresh');
      this.clear();
      throw new AuthenticationError('Failed to refresh token');
    }
  }

  async revoke(): Promise<void> {
    // Capture the token for the revoke request, then clear the local session
    // immediately so that isAuthenticated becomes false synchronously.
    const token = this.accessToken;
    this.clear();

    if (!this.apiUrl) {
      return;
    }

    log.info('Revoking refresh token');
    try {
      await fetch(`${this.apiUrl}/auth/revokeToken`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({}),
        credentials: 'include',
      });
    } catch {
      log.warn('Revoke request failed (best-effort)');
    }
  }

  clear(): void {
    this.accessToken = null;
    sessionStore.getState().resetSession();
    log.debug('Session cleared');
  }

  async tryRestoreSession(): Promise<boolean> {
    if (this.accessToken) {
      return true;
    }

    try {
      await this.refresh();
      return true;
    } catch {
      return false;
    }
  }
}

export const tokenManager = new TokenManager();
